import React, { FunctionComponent, useEffect } from 'react';

import { useFetch } from '@api/ApiHelper';
import { ShareManifestPutResponse } from '@api/v4/share_manifests/ShareManifestTypes';
import { StandardDialog } from '@components/library/dialog';
import { DangerButton } from '@components/library/button';
import LogRocket, { track as trackLogRocket, TrackedEvent } from '@helpers/logRocket';

import classes from './styles/bulk_share_link_asset_removal_modal.module.scss';

interface BulkShareLinkAssetRemovalProps {
  assetKeys: string[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const BulkShareLinkAssetRemovalModal: FunctionComponent<BulkShareLinkAssetRemovalProps> = ({
  assetKeys,
  open,
  setOpen,
}) => {
  const removeAssetsFetch = useFetch<ShareManifestPutResponse>({
    body: {
      data: {
        attributes: {
          asset_keys: assetKeys
        }
      }
    },
    fetchOnMount: false,
    method: 'POST',
    url: `/api/v4/share_manifests/${BFG.manifestDigest}/remove_assets`
  });
  const plural = assetKeys.length > 1;

  useEffect(() => {
    if (open) {
      trackLogRocket(TrackedEvent.ShareLinkAssetRemoval);
    }
  }, [open]);

  useEffect(() => {
    if (removeAssetsFetch.error) {
      Notify.create({
        title: bfTranslate('There was an error removing assets from your Share Link. Please try again.'),
        type: 'error'
      });
      LogRocket.captureMessage("Error removing assets", {
        tags: {},
        extra: { assetKeys }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeAssetsFetch.error]);

  useEffect(() => {
    if (removeAssetsFetch.response) {
      Notify.create({
        title: bfTranslate('Successfully removed assets from your Share Link. Reloading...'),
        type: 'success'
      });
      window.location.reload();
    }
  }, [removeAssetsFetch.response, setOpen]);

  return (
    <StandardDialog
      dialogBodyClassName={classes['bulk-share-links-removal']}
      height={370}
      id="bulk-share-links-asset-removal-dialog"
      open={open}
      setOpen={setOpen}
      showFooter={false}
      title={plural ? bfTranslate("Remove assets") : bfTranslate("Remove asset")}
      titleIcon="bff-trash"
      width={660}
    >
      <div className={classes['bulk-share-links-removal']}>
        <h3 className={classes['bulk-share-links-removal__heading']}>
          {bfTranslate('Are you sure you want to remove')}
          {' '}
          <strong>{assetKeys.length}{' '}{plural ? bfTranslate('assets') : bfTranslate('asset')}</strong>
          {' '}
          {bfTranslate('from your Share Link?')}
        </h3>
        <p className={classes['bulk-share-links-removal__hint']}>{plural
          ? bfTranslate('These assets will no longer be accessible to users from the Share Link.')
          : bfTranslate('This asset will no longer be accessible to users from the Share Link.')}
        </p>
        <div className={classes['bulk-share-links-removal__buttons-container']}>
          <DangerButton
            className={classes['bulk-share-links-removal__remove-button']}
            disabled={!!removeAssetsFetch.response}
            icon="bff-trash"
            isLoading={removeAssetsFetch.loading}
            loadingCopy={plural ? bfTranslate('Removing assets') : bfTranslate('Removing asset')}
            onClick={(): Promise<void> => removeAssetsFetch.fetch()}
          >
            {plural ? bfTranslate('Remove assets') : bfTranslate('Remove asset')}
          </DangerButton>
        </div>
      </div>
    </StandardDialog>
  );
};
